import '@/theme/partials/_button.scss';

import { Capacitor } from '@capacitor/core';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonImg,
  IonInfiniteScroll,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonRow,
  IonSpinner,
  IonText,
  IonToolbar,
} from '@ionic/react';
import classNames from 'classnames';
import { closeOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import ArrowRightMobile from '@/assets/icons/arrow-right-mobile.svg';
import DetailList from '@/assets/icons/detail-list.svg';
import Download from '@/assets/icons/download.svg';
import Info from '@/assets/icons/info.svg';
import ArrowLeft from '@/assets/icons/keyboard-arrow-left-black.svg';
import emptyStateImg from '@/assets/imgs/empty-reporting.svg';
import Vectors from '@/assets/imgs/vectors.svg';
import Banner from '@/components/common/Banner';
import { BannerType } from '@/components/common/Banner/Banner';
import { fullMonths, ROUTES_PATH } from '@/constants';
import {
  LiquidationsList,
  SettlementsAPIData,
  SettlementsDetailList,
} from '@/interfaces/ReportingInterface/ReportingInterface';
import { OrderType, SortType } from '@/redux/operation/operationsInterface';
import {
  saveSettlementDetail,
  saveSettlementDetailList,
  saveSettlements,
  selectCurrentSettlements,
} from '@/redux/reporting/reporting';
import {
  getSettlementDetail,
  getSettlements,
} from '@/redux/reporting/reportingAPI';
import { DashboardSettlements } from '@/redux/reporting/reportingInterface';
import { fileOpenerService } from '@/services/fileOpener';
import { RootState } from '@/store';
import { addToBOM, downloadCsv, exportToCsv } from '@/utils/csvExporter';
import {
  getMonthDays,
  transformAmount,
  transformDate,
  transformMonthToString,
} from '@/utils/settlementsHelper';

import styles from './MonthSettlements.module.scss';

export default function MonthSettlements() {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const [sortOrder, setSortOrder] = useState<SortType>(SortType.DESC);
  const [openInfoModal, setOpenInfoModal] = useState<boolean>(false);
  const [modalData, setModalData] = useState<{
    transferred_amount: number;
    compensated_amount: number;
  } | null>(null);
  const [loadingSettlements, setLoadingSettlements] = useState<boolean>(false);
  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [filterValues, setFilterValues] = useState<any>();

  const storedSettlements = useSelector(
    (state: RootState) => state.settlement.data.settlements,
  );
  const currentSettlements = useSelector(
    (state: RootState) => state.settlement.data.currentSettlements,
  );

  const settlementsDetailList = useSelector(
    (state: RootState) => state.settlement.data.settlementDetailList,
  );

  const openSettlementDetail = async (
    settlementId: string,
    paymentDate: string,
  ) => {
    const liquidationStored = settlementsDetailList.find(
      (settlement: SettlementsDetailList) => settlement.list[0]?.payment_date === paymentDate,
    );
    if (liquidationStored) {
      dispatch(saveSettlementDetail(liquidationStored));
      history.push(`${ROUTES_PATH.SETTLEMENT_DETAIL}/${settlementId}`);
    } else {
      setLoadingPage(true);
      const params: SettlementsAPIData = {
        offset: 0,
        limit: 30,
        from_date: `&from_date=${paymentDate}`,
        to_date: `&to_date=${paymentDate}`,
      };
      setFilterValues(params);
      const settlementSelected = await getSettlementDetail(
        settlementId,
        params,
      );
      const newDetail = {
        list: [...settlementSelected.data.settlements_detail],
        more_items: settlementSelected.metadata?.paging.more_items,
      };
      dispatch(saveSettlementDetail(newDetail));
      dispatch(saveSettlementDetailList([...settlementsDetailList, newDetail]));
      history.push(`${ROUTES_PATH.SETTLEMENT_DETAIL}/${settlementId}`);
      setLoadingPage(false);
    }
  };

  const orderByDate = async () => {
    const updatedSortOrder = sortOrder === SortType.ASC ? SortType.DESC : SortType.ASC;
    if (currentSettlements) {
      setLoadingSettlements(true);
      const monthString = transformMonthToString(currentSettlements.month);
      const monthDays = getMonthDays(
        currentSettlements.month,
        currentSettlements.year,
      );
      const params: SettlementsAPIData = {
        offset: 0,
        limit: 30,
        from_date: `&from_date=${currentSettlements.year}-${monthString}-01`,
        to_date: `&to_date=${currentSettlements.year}-${monthString}-${monthDays}`,
      };
      const response = await getSettlements(params, {
        order: OrderType.DATE,
        sort: updatedSortOrder,
      });
      const liquidations = {
        month: currentSettlements.month,
        total: currentSettlements.total,
        year: currentSettlements.year,
        list: response.data.settlements,
      };
      const prevSettlementIndex = storedSettlements.findIndex(
        (settlement: LiquidationsList) => settlement.month === currentSettlements.month
          && settlement.year === currentSettlements.year,
      );
      const newSettlements = [...storedSettlements];
      newSettlements.splice(prevSettlementIndex, 1, liquidations);
      setSortOrder(updatedSortOrder);
      dispatch(saveSettlements(newSettlements));
      dispatch(selectCurrentSettlements(liquidations));
      setLoadingSettlements(false);
    }
  };

  const searchNext = async ($event: CustomEvent<void>) => {
    setLoadingSettlements(true);
    const newOffset = currentPage + 30;
    const newFilterValues = {
      ...filterValues,
      offset: newOffset,
    };
    setCurrentPage(newOffset);
    setFilterValues(newFilterValues);
    const response = await getSettlements(newFilterValues);
    if (currentSettlements) {
      const newSettlements = {
        ...currentSettlements,
        list: [...currentSettlements.list, ...response.data.settlements],
        more_items: response.metadata?.paging.more_items,
      };
      dispatch(selectCurrentSettlements(newSettlements));
    }
    setLoadingSettlements(false);
    const target = $event.target as HTMLIonInfiniteScrollElement;
    target.complete();
  };

  const exportTable = () => {
    if (currentSettlements) {
      const settlementList: any[] = [];
      currentSettlements.list.map((settlement: DashboardSettlements) => {
        settlementList.push({
          Fecha: settlement.payment_date,
          Importe: `${settlement.financed_amount.toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })} €`,
          'Importe deducido': `${settlement.deduced_amount.toLocaleString(
            undefined,
            { minimumFractionDigits: 2 },
          )} €`,
          'Importe transferido': `${settlement.net_amount.toLocaleString(
            undefined,
            { minimumFractionDigits: 2 },
          )} €`,
        });
      });
      if (Capacitor.isNativePlatform()) {
        const settlementData = exportToCsv(settlementList);
        if (settlementData) {
          const csv = addToBOM(settlementData);
          fileOpenerService.openSettlementCsv(
            csv,
            `${currentSettlements.month}-${currentSettlements.year}`,
          );
        }
      } else {
        downloadCsv(settlementList);
      }
    }
  };

  const pageReturn = () => {
    history.push(ROUTES_PATH.REPORTING);
  };

  const retrieveSettlements = async () => {
    const pathname = location.pathname.split('/');
    const yearMonth = pathname[pathname.length - 1].split('-');
    const currentYear = parseInt(yearMonth[0]);
    const currentMonth = yearMonth[1];
    const monthDays = getMonthDays(parseInt(currentMonth), currentYear);
    const params: SettlementsAPIData = {
      offset: 0,
      limit: 30,
      from_date: `&from_date=${currentYear}-${currentMonth}-01`,
      to_date: `&to_date=${currentYear}-${currentMonth}-${monthDays}`,
    };
    const response = await getSettlements(params);
    const liquidations = {
      month: parseInt(currentMonth),
      total: response.data.settlements.length,
      year: currentYear,
      list: response.data.settlements,
      more_items: response.metadata?.paging.more_items,
    };
    dispatch(selectCurrentSettlements(liquidations));
    dispatch(saveSettlements([...storedSettlements, liquidations]));
    setFilterValues(params);
    setLoadingSettlements(false);
    setLoadingPage(false);
  };

  const openModal = (transferredAmount: number, compensatedAmount: number) => {
    setModalData({
      transferred_amount: transferredAmount,
      compensated_amount: compensatedAmount,
    });
    setOpenInfoModal(true);
  };

  useEffect(() => {
    if (currentSettlements) {
      const monthString = transformMonthToString(currentSettlements.month);
      const monthDays = getMonthDays(
        currentSettlements.month,
        currentSettlements.year,
      );
      setFilterValues({
        offset: 0 + Math.floor(currentSettlements.list.length / 30),
        limit: 30,
        from_date: `&from_date=${currentSettlements.year}-${monthString}-01`,
        to_date: `&to_date=${currentSettlements.year}-${monthString}-${monthDays}`,
      });
      setLoadingPage(false);
    } else {
      setLoadingPage(true);
      retrieveSettlements();
    }
  }, []);

  return (
    <IonPage className={styles.monthSettlementPage}>
      <IonContent scrollEvents className={styles.reportingContent}>
        <Banner type={BannerType.REPORTING} />
        <div className={styles.settlementTableContent}>
          <IonButton className={styles.reportingReturnBtn} onClick={pageReturn}>
            <IonImg src={ArrowLeft} className={styles.reportingReturnIcon} />
            Volver a reporting
          </IonButton>
          <div className={styles.reportingTableWrapper}>
            <div className={styles.reportingMainCard}>
              {loadingPage && (
                <div className={styles.reportingLoadingContainer}>
                  <div className={styles.customSpinner}>
                    <IonSpinner
                      color="danger"
                      name="bubbles"
                      className={styles.scrollSpinner}
                    />
                    <span className={styles.spinnerText}>Cargando...</span>
                  </div>
                </div>
              )}
              <div
                className={classNames(
                  styles.titleWrapper,
                  styles.reportingSettlementTitle,
                )}
              >
                <div className={styles.settlementTitleWrapper}>
                  <p className={styles.titleText}>
                    Liquidaciones -
                    {' '}
                    {currentSettlements
                      && fullMonths[currentSettlements.month - 1]}
                    {' '}
                    {currentSettlements?.year}
                  </p>
                  <IonButtons className={styles.reportingDownloadBtnWrapper}>
                    <IonButton
                      className={classNames(
                        'secondaryButton',
                        styles.reportingDetailBtn,
                        styles.reportingDownloadBtn,
                      )}
                      onClick={exportTable}
                    >
                      <IonText>Descargar listado</IonText>
                      <IonImg src={Download} />
                    </IonButton>
                  </IonButtons>
                </div>
                <hr className={styles.reportingDetailLine} />
              </div>
              {!loadingPage ? (
                <>
                  <IonList className={styles.settlementListDesktop}>
                    <IonGrid className={styles.reportingGridWrapper}>
                      <IonRow className={styles.reportingTableRow}>
                        <IonCol>
                          <IonRow key="table-date-title">
                            <IonLabel
                              onClick={() => orderByDate()}
                              className={classNames(
                                styles.generalTableTitle,
                                styles.sortAvailable,
                              )}
                            >
                              Fecha
                            </IonLabel>
                            <IonImg
                              src={Vectors}
                              onClick={() => orderByDate()}
                              className={styles.sortAvailable}
                            />
                          </IonRow>
                        </IonCol>
                        <IonCol>
                          <IonRow key="table-amount-title">
                            <IonLabel className={styles.generalTableTitle}>
                              Importe
                            </IonLabel>
                          </IonRow>
                        </IonCol>
                        <IonCol>
                          <IonRow key="table-deduced-amount-title">
                            <IonLabel className={styles.generalTableTitle}>
                              Importe deducido
                            </IonLabel>
                          </IonRow>
                        </IonCol>
                        <IonCol>
                          <IonRow key="table-transferred-amount-title">
                            <IonLabel className={styles.generalTableTitle}>
                              Importe transferido
                            </IonLabel>
                          </IonRow>
                        </IonCol>
                        <IonCol>
                          <IonRow key="table-detail-title">
                            <IonLabel className={styles.generalTableTitle}>
                              Ampliar detalle
                            </IonLabel>
                          </IonRow>
                        </IonCol>
                      </IonRow>
                    </IonGrid>
                    <IonGrid>
                      {currentSettlements?.list.map(
                        (settlement: DashboardSettlements) => (
                          <IonRow
                            key={currentSettlements.list.indexOf(settlement)}
                            className={styles.reportingTableRowData}
                          >
                            <IonCol className={styles.paymentDateTableCol}>
                              <IonLabel>
                                {transformDate(settlement.payment_date)}
                              </IonLabel>
                            </IonCol>
                            <IonCol className={styles.amountTableCol}>
                              <IonLabel>
                                {transformAmount(settlement.financed_amount)}
                              </IonLabel>
                            </IonCol>
                            <IonCol className={styles.deducedAmountTableCol}>
                              <IonLabel>
                                {transformAmount(settlement.deduced_amount)}
                              </IonLabel>
                            </IonCol>
                            <IonCol className={styles.transferredTableCol}>
                              <IonRow
                                className={styles.transferredTableContent}
                              >
                                <IonLabel
                                  className={styles.transferredTableAmount}
                                >
                                  {transformAmount(settlement.net_amount)}
                                </IonLabel>
                                <IonIcon
                                  src={Info}
                                  className={styles.transferredInfoIcon}
                                  onClick={() => openModal(
                                    settlement?.transferred_amount,
                                    settlement?.compensated_amount,
                                  )}
                                />
                                <IonModal
                                  isOpen={openInfoModal}
                                  onDidDismiss={() => {
                                    setOpenInfoModal(false);
                                    setModalData(null);
                                  }}
                                  className={styles.reportingInfoModal}
                                >
                                  <IonHeader>
                                    <IonToolbar
                                      className={styles.reportingHeaderModal}
                                    >
                                      <IonButtons slot="end">
                                        <IonButton
                                          className={styles.reportingCloseModal}
                                          onClick={() => {
                                            setOpenInfoModal(false);
                                            setModalData(null);
                                          }}
                                        >
                                          <IonIcon
                                            className={styles.modalCloseBtn}
                                            md={closeOutline}
                                          />
                                        </IonButton>
                                      </IonButtons>
                                    </IonToolbar>
                                  </IonHeader>
                                  <div className={styles.reportingModalContent}>
                                    <IonRow
                                      className={
                                        styles.reportingModalTitleWrapper
                                      }
                                    >
                                      <IonImg src={Info} />
                                      <div
                                        className={classNames(
                                          styles.titleText,
                                          styles.reportingModalTitle,
                                        )}
                                      >
                                        Detalle de importes
                                      </div>
                                    </IonRow>
                                    <IonRow
                                      className={styles.reportingModalRow}
                                    >
                                      <IonLabel>Importe activaciones</IonLabel>
                                      <IonLabel>
                                        {modalData
                                          && `${modalData?.transferred_amount.toLocaleString(
                                            'de-DE',
                                            {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            },
                                          )} €`}
                                      </IonLabel>
                                    </IonRow>
                                    <IonRow
                                      className={styles.reportingModalRow}
                                    >
                                      <IonLabel>
                                        Importe compensaciones
                                      </IonLabel>
                                      <IonLabel>
                                        {modalData
                                          && `${modalData?.compensated_amount.toLocaleString(
                                            'de-DE',
                                            {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            },
                                          )} €`}
                                      </IonLabel>
                                    </IonRow>
                                  </div>
                                </IonModal>
                              </IonRow>
                            </IonCol>
                            <IonCol className={styles.detailTableCol}>
                              {settlement.settlement_id
                                  !== '00000000-0000-0000-0000-000000000000' && (
                                  <IonLabel>
                                    <IonImg
                                      className={styles.liquidationDetailIcon}
                                      src={DetailList}
                                      onClick={() => {
                                        openSettlementDetail(
                                          settlement.settlement_id,
                                          settlement.payment_date,
                                        );
                                      }}
                                    />
                                  </IonLabel>
                              )}
                            </IonCol>
                          </IonRow>
                        ),
                      )}
                    </IonGrid>
                  </IonList>
                  <IonInfiniteScroll
                    id="month-settlement-scroll"
                    threshold="25px"
                    disabled={!currentSettlements?.more_items}
                    onIonInfinite={(e: CustomEvent<void>) => searchNext(e)}
                    className={styles.scrollWrapper}
                  >
                    {currentSettlements?.more_items && loadingSettlements && (
                      <IonSpinner
                        color="danger"
                        name="bubbles"
                        className={styles.scrollSpinner}
                      />
                    )}
                  </IonInfiniteScroll>
                </>
              ) : (
                <div className={styles.reportingTableEmptySpace}>
                  <div>
                    <img src={emptyStateImg} alt="no liquidations" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.settlementTableResponsive}>
          {loadingPage && (
            <div className={styles.reportingLoadingContainerResponsive}>
              <div className={styles.customSpinner}>
                <IonSpinner
                  color="danger"
                  name="bubbles"
                  className={styles.scrollSpinner}
                />
                <span className={styles.spinnerText}>Cargando...</span>
              </div>
            </div>
          )}
          <div className={styles.settlementTableContainer}>
            <div className={styles.reportingTitleWrapper}>
              <p className={styles.titleText}>Reporting</p>
            </div>
            <div className={styles.reportingTitleResponsive}>
              <IonButton
                className={styles.reportingReturnBtn}
                onClick={pageReturn}
              >
                <IonImg
                  src={ArrowLeft}
                  className={styles.reportingReturnIcon}
                />
              </IonButton>
              <p
                className={classNames(
                  styles.titleText,
                  styles.reportingSettlementTitle,
                )}
              >
                Liquidaciones -
                {' '}
                {currentSettlements && fullMonths[currentSettlements.month - 1]}
                {' '}
                {currentSettlements?.year}
              </p>
            </div>
            {!loadingPage && (
              <div className={styles.settlementTableWrapperResponsive}>
                <IonList className={styles.settlementListResponsive}>
                  <IonGrid className={styles.reportingGridWrapper}>
                    {currentSettlements?.list.map(
                      (settlement: DashboardSettlements) => (
                        <IonRow
                          key={currentSettlements.list.indexOf(settlement)}
                          className={styles.reportingTableMobileCard}
                        >
                          <IonRow
                            className={styles.reportingMobileCardContent}
                            onClick={() => {
                              if (
                                settlement.settlement_id
                                !== '00000000-0000-0000-0000-000000000000'
                              ) {
                                openSettlementDetail(
                                  settlement.settlement_id,
                                  settlement.payment_date,
                                );
                              }
                            }}
                          >
                            <IonCol>
                              <IonRow
                                className={styles.reportingTableMobileDate}
                              >
                                Fecha:
                                {' '}
                                {transformDate(settlement.payment_date)}
                              </IonRow>
                              <IonRow
                                className={styles.reportingTableMobileAmount}
                              >
                                Transferido:
                                {' '}
                                {transformAmount(settlement.net_amount)}
                              </IonRow>
                              <IonRow
                                className={styles.reportingTableMobileSubtitle}
                              >
                                Activaciones:
                                {transformAmount(settlement.transferred_amount)}
                              </IonRow>
                              <IonRow
                                className={styles.reportingTableMobileSubtitle}
                              >
                                Compensaciones:
                                {transformAmount(settlement.compensated_amount)}
                              </IonRow>
                            </IonCol>
                            <IonCol className={styles.reportingTableMobileBtn}>
                              {settlement.settlement_id !== '00000000-0000-0000-0000-000000000000' && (
                                <IonLabel>
                                  <IonImg
                                    className={styles.liquidationDetailIconMobile}
                                    src={ArrowRightMobile}
                                  />
                                </IonLabel>
                              )}
                            </IonCol>
                          </IonRow>
                          <IonRow>
                            <hr className={styles.reportingDetailLine} />
                          </IonRow>
                        </IonRow>
                      ),
                    )}
                  </IonGrid>
                </IonList>
                <IonInfiniteScroll
                  id="month-settlement-scroll-mobile"
                  threshold="25px"
                  disabled={!currentSettlements?.more_items}
                  onIonInfinite={(e: CustomEvent<void>) => {
                    setLoadingSettlements(true);
                    searchNext(e);
                  }}
                  className={styles.scrollWrapper}
                >
                  {currentSettlements?.more_items && loadingSettlements && (
                    <IonSpinner
                      color="danger"
                      name="bubbles"
                      className={styles.scrollSpinner}
                    />
                  )}
                </IonInfiniteScroll>
              </div>
            )}
          </div>
          {!loadingPage ? (
            <div className={styles.reportingDownloadBtnWrapper}>
              <IonButtons>
                <IonButton
                  className={classNames(
                    'secondaryButton',
                    styles.reportingDetailBtn,
                    styles.reportingDownloadBtn,
                  )}
                  onClick={exportTable}
                >
                  <IonText>Descargar listado</IonText>
                  <IonImg src={Download} />
                </IonButton>
              </IonButtons>
            </div>
          ) : (
            <div className={styles.reportingTableEmptySpace}>
              <div>
                <img src={emptyStateImg} alt="no liquidations" />
              </div>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
